import React from "react";
import {
  Box,
  IconButton,
  Popover,
  useTheme,
  TextFieldProps,
  Badge,
  Typography,
  Theme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useQuery } from "@tanstack/react-query";
import {
  usePopupState,
  bindTrigger,
  bindPopover,
} from "material-ui-popup-state/hooks";

import { SearchField } from "../fields/SearchField";
import ClientExercisesList from "./ClientExercisesList";
import {
  ExerciseTagDto,
  UserExerciseDto,
} from "@growth-machine-llc/stridist-api-client";
import { NONE_TAG_ID } from "../exercise-library/filters/TagsFilters";
import ExerciseTagsService from "../../services/ExerciseTagsService";
import { ALL_OPTIONS } from "../../contexts/ExerciseLibraryFilterContext";
import {
  ALL_EXERCISE_TAGS_QUERY_KEY,
  allBodyparts,
  allBodypartsSelected,
  allMovementPatterns,
  allMovementPatternsSelected,
} from "../exercise-library/ExercisesTableScreen";
import { useUserIsClient } from "../../hooks/useCurrentUser";
import { FilterIcon, X as CloseIcon } from "lucide-react";
import { ExerciseFiltersAccordion } from "./ExerciseAccordionFilters";
import { useClientExercises } from "./ClientExercisesContext";
import { JourneyAnchors } from "../../constants";
import clsx from "clsx";

const allTagsSelected = (
  tagIds: number[] | undefined,
  exerciseTags: ExerciseTagDto[] | undefined,
): boolean => {
  if (!exerciseTags || !tagIds) return false;

  const allIds = [...exerciseTags.map((t) => t.id), NONE_TAG_ID];
  return allIds.every((id) => tagIds.includes(id));
};

interface ThemeOptions {
  modalView: boolean;
}

const useStyles = makeStyles<Theme, ThemeOptions>((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minWidth: 320,
    maxWidth: 320,
    height: ({ modalView }) => (modalView ? "80vh" : "100vh"),
    position: "sticky",
    top: 0,
    padding: ({ modalView }) => (!modalView ? theme.spacing(2, 0) : 0),
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    borderTop: ({ modalView }) => modalView && "none",
  },
  inputActive: {
    "& .MuiOutlinedInput-root fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
  selectCategory: {
    maxHeight: 300,
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2, 0, 2),
  },
}));

interface IClientExercisesSearchProps {
  clientId: number;
  onExerciseSelect: (exercise: UserExerciseDto) => void;
  selectedExerciseId?: number;
  modalView?: boolean;
  onModalClose?: () => void;
}

const defaultQuery = "";
const filterInputHeightProps = { minHeight: 40, height: 40, borderRadius: 8 };

const ClientExercisesSearch = ({
  clientId,
  onExerciseSelect,
  selectedExerciseId,
  modalView = false,
  onModalClose,
}: IClientExercisesSearchProps) => {
  const classes = useStyles({ modalView });
  const theme = useTheme();
  const isClient = useUserIsClient();

  const { filters, updateFilters, setSelectedExercise } = useClientExercises();

  const [activeFilters, setActiveFilters] = React.useState(0);

  const { data: exerciseTags } = useQuery({
    queryKey: [ALL_EXERCISE_TAGS_QUERY_KEY],
    queryFn: () => ExerciseTagsService.getAllExerciseTags(),
    enabled: !isClient,
  });

  const popupState = usePopupState({
    variant: "popover",
    popupId: "filtersPopover",
  });

  const handleQueryChange: TextFieldProps["onChange"] = React.useCallback(
    (event) => updateFilters({ query: event.currentTarget.value }),
    [updateFilters],
  );

  const handleExerciseClick = React.useCallback(
    (_: any, value: UserExerciseDto) => {
      setSelectedExercise(value);
      onExerciseSelect(value);
    },
    [setSelectedExercise, onExerciseSelect],
  );

  const handleApplyFilters = () => {
    popupState.close();
  };

  const bodyparts =
    filters.bodyparts === ALL_OPTIONS ? allBodyparts : filters.bodyparts;

  const movementPatterns =
    filters.movementPatterns === ALL_OPTIONS
      ? allMovementPatterns
      : filters.movementPatterns;

  const tagIds =
    filters.tagIds === ALL_OPTIONS
      ? exerciseTags && exerciseTags.length > 0
        ? [...exerciseTags.map((t) => t.id), NONE_TAG_ID]
        : null
      : filters.tagIds;

  return (
    <Box className={classes.root}>
      <Box>
        {modalView && (
          <Box className={classes.modalHeader}>
            <Typography fontSize={16} fontWeight={600}>
              Select an exercise
            </Typography>
            <IconButton onClick={onModalClose}>
              <CloseIcon color={theme.palette.text.secondary} />
            </IconButton>
          </Box>
        )}
        <Box className={classes.searchBox}>
          <SearchField
            className={clsx(
              filters.query !== defaultQuery ? classes.inputActive : "",
              JourneyAnchors.EXERCISE_SEARCH,
            )}
            variant="outlined"
            fullWidth
            placeholder="Search by exercises"
            value={filters.query ?? ""}
            onChange={handleQueryChange}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderRadius: "8px",
                },
                ...filterInputHeightProps,
              },
            }}
          />
          <IconButton
            {...bindTrigger(popupState)}
            className={JourneyAnchors.EXERCISE_SEARCH_FILTER_BUTTON}
          >
            <Badge
              badgeContent={activeFilters > 0 ? activeFilters : undefined}
              color="secondary"
              sx={{
                "& .MuiBadge-badge": {
                  right: -3,
                  top: -3,
                },
              }}
            >
              <FilterIcon color={theme.palette.text.secondary} />
            </Badge>
          </IconButton>
        </Box>
        <Popover
          {...bindPopover(popupState)}
          sx={{
            "& .MuiPaper-root": { boxShadow: "none" },
            "& .MuiPopover-paper": {
              outline: (theme) => `1px solid ${theme.palette.divider}`,
            },
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: modalView ? "right" : "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: modalView ? "right" : "center",
          }}
        >
          <Box display="flex" flexDirection="column">
            <ExerciseFiltersAccordion
              movementPatterns={movementPatterns}
              bodyparts={bodyparts}
              onApply={handleApplyFilters}
              exerciseTags={exerciseTags}
              selectedTagIds={tagIds}
              onMovementChange={(movementPatterns) =>
                updateFilters({
                  movementPatterns: allMovementPatternsSelected(
                    movementPatterns,
                  )
                    ? ALL_OPTIONS
                    : movementPatterns,
                })
              }
              onBodypartChange={(bodyparts) =>
                updateFilters({
                  bodyparts: allBodypartsSelected(bodyparts)
                    ? ALL_OPTIONS
                    : bodyparts,
                })
              }
              onTagChange={(tagIds) =>
                updateFilters({
                  tagIds:
                    exerciseTags && allTagsSelected(tagIds, exerciseTags)
                      ? ALL_OPTIONS
                      : tagIds,
                })
              }
              onActiveCountChange={setActiveFilters}
            />
          </Box>
        </Popover>
      </Box>
      <ClientExercisesList
        onClick={handleExerciseClick}
        selectedExerciseId={selectedExerciseId}
        bodyparts={bodyparts}
        movementPatterns={movementPatterns}
        clientId={clientId}
        tagIds={isClient ? null : tagIds}
      />
    </Box>
  );
};

export default ClientExercisesSearch;
